// src/components/AppLayout.jsx
// Unified layout replacing AdminLayout, EmployeeLayout, and PreboardingLayout
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import UnifiedSidebar from './UnifiedSidebar';
import ActivityCentreSidebar from './ActivityCentreSidebar';
import NotificationDropdown from './Admin/NotificationDropdown';
import NoticePopupModal from './Notices/NoticePopupModal';
import TaskNotificationPopup from './TaskNotificationPopup';
import { Menu, LogOut, User, Settings, ChevronDown } from "lucide-react";
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { SurveyProvider } from '../context/SurveyContext';
import SurveyAIOverlay from './Surveys/Runtime/SurveyAIOverlay';
import { AIPanelProvider } from '../context/AIPanelContext';
import AIPanelManager from './AI/AIPanelManager';

const AppLayout = () => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [noticeUnreadCount, setNoticeUnreadCount] = useState(0);
    const [showPopupNotices, setShowPopupNotices] = useState(false);
    const profileDropdownRef = useRef(null);
    const navigate = useNavigate();
    const { logout, user } = useAuth();

    // Detect preboarding context from URL
    const { token } = useParams();
    const location = useLocation();
    const isPreboarding = location.pathname.includes('/preboard/');
    const isSetupPage = location.pathname === '/admin/setup';
    const isAdmin = user?.is_admin;

    // Determine basePath for employee routes
    const basePath = isPreboarding ? `/preboard/${token}/dashboard` : '/employee';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setProfileDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch notice unread count (employee/preboarding only)
    useEffect(() => {
        if (isAdmin) return;
        const fetchNoticeUnreadCount = async () => {
            try {
                const response = await api.get('/notices/employee/unread-count');
                setNoticeUnreadCount(response.data.unreadCount || 0);
            } catch (error) {
                console.error('Error fetching unread count:', error);
            }
        };
        fetchNoticeUnreadCount();
        setShowPopupNotices(true);
    }, [isAdmin]);

    const handleLogout = () => {
        setProfileDropdownOpen(false);
        logout();
        navigate('/login');
    };

    // ─── Display name / initials ──────────────────────
    const getDisplayName = () => {
        if (isAdmin) {
            return user?.company_name || 'Admin';
        }
        if (user?.first_name && user?.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        if (user?.first_name) return user.first_name;
        return 'User';
    };

    const getInitials = () => {
        if (isAdmin) {
            if (user?.company_name) {
                const words = user.company_name.split(' ').filter(w => w.length > 0);
                if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
                return user.company_name.substring(0, 2).toUpperCase();
            }
            return 'AD';
        }
        if (user?.first_name && user?.last_name) {
            return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
        }
        if (user?.first_name) return user.first_name[0].toUpperCase();
        return 'U';
    };

    const getRoleLabel = () => {
        if (isAdmin) return 'Administrator';
        return user?.position || user?.role_name || 'Employee';
    };

    const settingsPath = isAdmin ? '/admin/settings' : `${basePath}/settings`;

    return (
        <AIPanelProvider>
            <SurveyProvider>
                <div className="min-h-screen bg-background">
                    {/* Unified Sidebar */}
                    {!isSetupPage && (
                        <UnifiedSidebar
                            isCollapsed={sidebarCollapsed}
                            setIsCollapsed={setSidebarCollapsed}
                            isMobileOpen={mobileMenuOpen}
                            setIsMobileOpen={setMobileMenuOpen}
                            basePath={basePath}
                        />
                    )}

                    {/* Activity Centre Secondary Sidebar (admin only, auto-hidden on non-activity routes) */}
                    {!isSetupPage && isAdmin && <ActivityCentreSidebar sidebarCollapsed={sidebarCollapsed} />}

                    {/* Main Content Area */}
                    <div
                        className={`
                        min-h-screen bg-background transition-all duration-300
                        ${isSetupPage ? '' : sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
                    `}
                    >
                        {/* Header */}
                        {!isSetupPage && (
                            <header className="bg-card border-b border-border sticky top-0 z-30">
                                <div className="flex items-center justify-between h-16 px-4 lg:px-6">
                                    <div className="flex items-center gap-4">
                                        {!isSetupPage && (
                                            <button
                                                onClick={() => setMobileMenuOpen(true)}
                                                className="lg:hidden p-2 hover:bg-secondary rounded-lg"
                                            >
                                                <Menu size={24} className="text-muted-foreground" />
                                            </button>
                                        )}
                                        <h1 className="text-xl font-semibold text-foreground hidden lg:block">Dashboard</h1>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {/* Spacer for search bar area */}
                                        <div className="hidden md:flex items-center w-64"></div>

                                        {/* Notifications */}
                                        <NotificationDropdown />

                                        {/* Profile with Dropdown */}
                                        <div className="relative" ref={profileDropdownRef}>
                                            <button
                                                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                                                className="flex items-center gap-3 pl-3 border-l border-border hover:bg-secondary rounded-lg pr-2 py-1 transition-colors"
                                            >
                                                <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-sm">
                                                    {getInitials()}
                                                </div>
                                                <div className="hidden sm:block text-left">
                                                    <p className="text-sm font-semibold text-foreground">{getDisplayName()}</p>
                                                    <p className="text-xs text-muted-foreground">{getRoleLabel()}</p>
                                                </div>
                                                <ChevronDown
                                                    size={16}
                                                    className={`text-muted-foreground hidden sm:block transition-transform ${profileDropdownOpen ? 'rotate-180' : ''}`}
                                                />
                                            </button>

                                            {/* Dropdown Menu */}
                                            {profileDropdownOpen && (
                                                <div className="absolute right-0 mt-2 w-56 bg-card rounded-xl shadow-lg border border-border py-2 z-50">
                                                    {/* User Info Header */}
                                                    <div className="px-4 py-3 border-b border-border">
                                                        <p className="text-sm font-semibold text-foreground">{getDisplayName()}</p>
                                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                                    </div>

                                                    {/* Menu Items */}
                                                    <div className="py-1">
                                                        <button
                                                            onClick={() => {
                                                                setProfileDropdownOpen(false);
                                                                // Navigate to profile page when implemented
                                                            }}
                                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                                                        >
                                                            <User size={18} className="text-muted-foreground" />
                                                            View Profile
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setProfileDropdownOpen(false);
                                                                navigate(settingsPath);
                                                            }}
                                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                                                        >
                                                            <Settings size={18} className="text-muted-foreground" />
                                                            Settings
                                                        </button>
                                                    </div>

                                                    {/* Logout */}
                                                    <div className="border-t border-border pt-1">
                                                        <button
                                                            onClick={handleLogout}
                                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                        >
                                                            <LogOut size={18} className="text-red-500" />
                                                            Log Out
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </header>
                        )}

                        <Outlet />

                        {/* Powered by Envion Footer */}
                        {!isSetupPage && (
                            <footer className="py-3 px-6 text-center text-xs text-muted-foreground border-t border-border mt-auto">
                                Powered by <span className="font-semibold text-accent">envion</span>
                            </footer>
                        )}
                    </div>

                    {/* Notice Popup Modal (employee/preboarding only) */}
                    {!isAdmin && (
                        <NoticePopupModal
                            isOpen={showPopupNotices}
                            onClose={() => {
                                setShowPopupNotices(false);
                                // Refresh unread count after dismissing
                                api.get('/notices/employee/unread-count')
                                    .then(r => setNoticeUnreadCount(r.data.unreadCount || 0))
                                    .catch(() => { });
                            }}
                        />
                    )}

                    {/* Task Notification Popup (employee/preboarding only) */}
                    {!isAdmin && <TaskNotificationPopup />}

                    {/* Survey AI Overlay (employee only) */}
                    {!isAdmin && <SurveyAIOverlay />}

                    {/* AI Panel Manager (admin only, persists across routes) */}
                    {isAdmin && <AIPanelManager />}
                </div>
            </SurveyProvider>
        </AIPanelProvider>
    );
};

export default AppLayout;
